import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { SalesAssistantService } from './api/sales-assistant.service';
import { Crypto } from './helpers/encryption-utils';
import { LocalStorageService } from './helpers/local-storage.service';
import { TokenService } from './helpers/token.service';
import { clearPersistedStore } from '@state-management/middleware/redux-hydration';

/** The agent a Sales Assistant is currently impersonating (subset of the act-as response). */
export interface ActingAsAgent {
  _id: string;
  full_name?: string;
  email?: string;
  photo?: string;
  active_office?: string;
  /** Module keys the agent granted this assistant (see ASSISTANT_ACCESS_MODULES). Absent = backend sent no scope, so nothing is restricted. */
  allowed_modules?: string[];
  /** Agent's approval of this assistant — only `approved` agents can be acted as. Absent on older payloads, treated as approved. */
  access_status?: 'pending' | 'approved' | 'revoked';
}

/** Snapshot of the assistant's own session, captured so we can restore it on exit. */
interface AssistantSession {
  token: string;
  uid: string;
  officeid: string;
  role: string;
}

/**
 * Orchestrates the "act as agent" flow for Sales Assistants.
 *
 * Design: this codebase keys the whole app off a handful of localStorage values —
 * `token` (read by AuthTokenInterceptor), and `uid`/`officeid`/`role`/`_per`
 * (written by AuthService.setAuthUser and read by ApiService/DashboardService/etc).
 * So "becoming" an agent is just: swap the stored `token` for the act-as token,
 * re-fetch `/me`, and let setAuthUser rebuild everything on reload.
 *
 * We keep the assistant's own session in `assistant_session` so "Exit" can restore it,
 * and cache the assignable-agents list in `assistant_agents` so the switcher survives
 * a reload while acting (where `/me` returns the agent, who has no assistant permission).
 *
 * See SALES_ASSISTANT_FRONTEND.md §4.
 */
@Injectable({ providedIn: 'root' })
export class ActingAsService {
  private readonly ASSISTANT_SESSION_KEY = 'assistant_session';
  private readonly ACTING_AS_KEY = 'acting_as';
  private readonly ASSIGNED_AGENTS_KEY = 'assistant_agents';

  /** Emits the current acting-as agent (or null when on the assistant's own token). */
  private actingAsSubject = new BehaviorSubject<ActingAsAgent | null>(this.getActingAgent());
  actingAgent$ = this.actingAsSubject.asObservable();

  /** Guards against firing two act-as/exit flows at once (each ends in a page reload). */
  private busy = false;

  constructor(
    private salesAssistant: SalesAssistantService,
    private tokenService: TokenService,
    private localStorage: LocalStorageService,
    private toastr: ToastrService
  ) {}

  // ---- State readers -------------------------------------------------------

  /** True while the active token is an act-as (agent) token. */
  isActingAs(): boolean {
    return !!this.localStorage.GetItem(this.ACTING_AS_KEY);
  }

  /** The agent currently being impersonated, or null. */
  getActingAgent(): ActingAsAgent | null {
    const raw = this.localStorage.GetItem(this.ACTING_AS_KEY);
    return raw ? Crypto.decrypt(raw) : null;
  }

  /** Module keys the current agent granted, or null when not acting / no scope was sent (unrestricted). */
  getAllowedModules(): string[] | null {
    const allowed = this.isActingAs() ? this.getActingAgent()?.allowed_modules : null;
    return Array.isArray(allowed) ? allowed : null;
  }

  /**
   * Takes the live scope from a `/me` payload and stores it. The act-as response's scope goes stale
   * as soon as the agent changes their grants mid-session; `/me` re-reads the link record every time.
   */
  syncAllowedModules(user: any): void {
    const agent = this.getActingAgent();
    if (!agent || !user?.acting_as || !Array.isArray(user?.allowed_modules)) {
      return;
    }
    this.localStorage.SetItem(this.ACTING_AS_KEY, Crypto.encrypt({ ...agent, allowed_modules: user.allowed_modules }));
  }

  /** Cached list of agents this assistant may act as (persisted from the assistant-token fetch). */
  getAssignedAgentsCache(): ActingAsAgent[] {
    const raw = this.localStorage.GetItem(this.ASSIGNED_AGENTS_KEY);
    return raw ? Crypto.decrypt(raw) || [] : [];
  }

  /** Persist the assignable-agents list so the switcher keeps working across reloads while acting. */
  persistAssignedAgents(agents: ActingAsAgent[]): void {
    this.localStorage.SetItem(this.ASSIGNED_AGENTS_KEY, Crypto.encrypt(agents || []));
  }

  // ---- Flows ---------------------------------------------------------------

  /**
   * Act as (or switch to) `agent`. Mints an act-as token, stores it as the active token,
   * pre-seeds the agent's ids, and hard-reloads into the dashboard so all role/permission
   * state rebuilds under the agent's identity (§4.5).
   */
  setActiveAgent(agent: ActingAsAgent): void {
    if (this.busy || !agent?._id) {
      return;
    }
    this.busy = true;

    const prevActiveToken = this.tokenService.getToken();
    const alreadyActing = this.isActingAs();

    // The act-as request must be authenticated AS THE ASSISTANT, so grab the assistant
    // session (capturing it now on first act-as) and restore that token before minting.
    const session = alreadyActing ? this.getAssistantSession() : this.captureAssistantSession();
    if (!session?.token) {
      this.busy = false;
      this.toastr.error('Your session could not be verified. Please log in again.');
      return;
    }
    this.tokenService.setToken(session.token);

    this.salesAssistant.actAs(agent._id).subscribe({
      next: response => {
        const data = response?.data;
        if (!data?.access_token) {
          this.rollback(prevActiveToken, alreadyActing);
          this.toastr.error('Could not act as this agent.');
          return;
        }

        // Swap in the act-as token and record who we are acting as.
        this.tokenService.setToken(data.access_token);
        const actingAgent: ActingAsAgent = {
          ...(data.acting_as || agent),
          allowed_modules: data.allowed_modules ?? data.acting_as?.allowed_modules,
        };
        this.localStorage.SetItem(this.ACTING_AS_KEY, Crypto.encrypt(actingAgent));

        // Pre-seed identity so anything reading uid/officeid before /me returns already
        // scopes to the agent (dashboard routes, etc.). setAuthUser finalises the rest on reload.
        this.localStorage.SetItem('uid', Crypto.encrypt(actingAgent._id));
        if (actingAgent.active_office) {
          this.localStorage.SetItem('officeid', Crypto.encrypt(actingAgent.active_office));
        }

        this.actingAsSubject.next(actingAgent);
        this.reloadIntoDashboard();
      },
      error: err => {
        this.rollback(prevActiveToken, alreadyActing);
        this.toastr.error(this.actAsErrorMessage(err));
      },
    });
  }

  /**
   * Stop acting-as and return to the assistant's own account. Restores the saved
   * assistant token + identity and hard-reloads so `/me` rebuilds the assistant's state.
   */
  exitActingAs(): void {
    if (this.busy) {
      return;
    }
    this.busy = true;

    const session = this.getAssistantSession();
    if (session?.token) {
      this.tokenService.setToken(session.token);
      // Restore the assistant's identity so nothing reads the agent's ids after exit.
      if (session.uid) this.localStorage.SetItem('uid', Crypto.encrypt(session.uid));
      if (session.officeid) this.localStorage.SetItem('officeid', Crypto.encrypt(session.officeid));
      if (session.role) this.localStorage.SetItem('role', Crypto.encrypt(session.role));
    }

    this.localStorage.RemoveItem(this.ACTING_AS_KEY);
    this.localStorage.RemoveItem(this.ASSISTANT_SESSION_KEY);
    // Keep ASSIGNED_AGENTS_KEY — the switcher should still offer agents after exiting.

    this.actingAsSubject.next(null);
    this.reloadIntoDashboard();
  }

  // ---- Internals -----------------------------------------------------------

  private getAssistantSession(): AssistantSession | null {
    const raw = this.localStorage.GetItem(this.ASSISTANT_SESSION_KEY);
    return raw ? Crypto.decrypt(raw) : null;
  }

  /** Snapshot the assistant's own token + identity so Exit can restore them. */
  private captureAssistantSession(): AssistantSession {
    const session: AssistantSession = {
      token: this.tokenService.getToken(),
      uid: this.decryptItem('uid'),
      officeid: this.decryptItem('officeid'),
      role: this.decryptItem('role'),
    };
    this.localStorage.SetItem(this.ASSISTANT_SESSION_KEY, Crypto.encrypt(session));
    return session;
  }

  private decryptItem(key: string): string {
    const raw = this.localStorage.GetItem(key);
    return raw ? Crypto.decrypt(raw) : '';
  }

  /** Restore the previous active token after a failed act-as, and clean up a half-written session. */
  private rollback(prevActiveToken: string, wasAlreadyActing: boolean): void {
    if (prevActiveToken) {
      this.tokenService.setToken(prevActiveToken);
    }
    if (!wasAlreadyActing) {
      this.localStorage.RemoveItem(this.ASSISTANT_SESSION_KEY);
    }
    this.busy = false;
  }

  private actAsErrorMessage(err: any): string {
    if (err?.status === 403 && err?.error?.code === 'agent_approval_required') {
      return 'This agent has not approved your access yet.';
    }
    if (err?.status === 403) {
      return 'You are not permitted to act as this agent.';
    }
    if (err?.status === 404) {
      return 'This agent no longer exists or has been deactivated.';
    }
    return 'Could not act as this agent. Please try again.';
  }

  /**
   * Hard reload into the dashboard. A full reload (not just a re-fetch) is intentional:
   * it rebuilds route guards, sidebar menu, permission-gated UI and dashboard caches
   * cleanly under the new identity, matching the existing office-switch behaviour.
   */
  private reloadIntoDashboard(): void {
    // Drop the persisted NgRx slices (e.g. permission-derived sidebar menu) so the
    // new identity's menu is rebuilt cleanly instead of flashing the previous one.
    clearPersistedStore(this.localStorage);
    // App uses hash routing (e.g. `/#/login`); route to the dashboard root, then reload.
    window.location.hash = '/';
    window.location.reload();
  }
}
